import type { LegalContent } from './types';

export const legalEn: LegalContent = {
  terms: {
    title: 'Terms of Use',
    version: 'Version 1.0',
    effectiveDate: 'Effective date: 2026-08-18',
    draftNotice: 'This document is a preliminary version and should be reviewed by a lawyer before official publication.',
    sections: [
      {
        heading: '1. Subject',
        paragraphs: [
          'These Terms of Use ("Terms") govern the use of the Aura Nota platform ("Service"), a software for automated capture and extraction of fields from Brazilian tax documents (NFS-e, NF-e, telecommunications invoices and similar), provided by Felixtal, enrolled in the CNPJ under no. 07.888.306/0001-75 ("Aura Nota", "we", "us").',
          'The Service processes files (PDF, XML, images and emails) to extract structured data through OCR, XML parsing and language models, with self-learning capability from human corrections.',
        ],
      },
      {
        heading: '2. Registration and account',
        paragraphs: [
          'Access to the Service requires creating an account with email and password. The user is responsible for the accuracy of the data provided during registration and for maintaining the confidentiality of their credentials. Any activity performed with the user account is considered their responsibility.',
        ],
      },
      {
        heading: '3. User obligations',
        list: [
          'Provide truthful and up-to-date information during registration.',
          'Maintain the confidentiality of their access credentials.',
          'Use the Service in compliance with Brazilian legislation, including the LGPD (Law 13.709/2018).',
          'Not send documents whose processing violates third-party rights.',
          'Not attempt to compromise the security, integrity or availability of the Service.',
          'Review extracted data before using it for tax or accounting purposes, as automated extraction may contain errors.',
        ],
      },
      {
        heading: '4. Aura Nota obligations',
        list: [
          'Make the Service available with reasonable availability and continuity.',
          'Adopt technical and organizational security measures as described in the Privacy Policy.',
          'Process personal data in compliance with the LGPD.',
          'Maintain a record of user consent (Art. 8 of the LGPD).',
          'Provide a contact channel for questions and exercise of rights (web@felixtal.com).',
        ],
      },
      {
        heading: '5. Personal data',
        paragraphs: [
          'The processing of personal data in the Service is governed by the Privacy Policy, available at /privacy. By accepting these Terms, the user declares awareness of the purposes, legal bases and security measures described in that document.',
        ],
      },
      {
        heading: '6. Limitation of liability',
        paragraphs: [
          'The Service is provided "as is". Automated field extraction may contain inaccuracies; it is the user\'s responsibility to review results before any tax, accounting or legal use. Aura Nota is not liable for decisions made based on extracted data without human review.',
          'Under no circumstances shall Aura Nota be liable for indirect damages, lost profits or data loss arising from the use or inability to use the Service, to the extent permitted by Brazilian legislation.',
        ],
      },
      {
        heading: '7. Account suspension and termination',
        paragraphs: [
          'Aura Nota may suspend or terminate accounts that violate these Terms or that pose a risk to the Service\'s security. The user may request the closure of their account at any time via web@felixtal.com.',
        ],
      },
      {
        heading: '8. Modifications to the Terms',
        paragraphs: [
          'These Terms may be updated periodically. Material changes will be communicated to users, who may be required to provide new consent. The current version is always the one available on this page, with the effective date indicated.',
        ],
      },
      {
        heading: '9. Applicable law',
        paragraphs: [
          'These Terms are governed by the laws of the Federative Republic of Brazil, including the General Data Protection Law (LGPD, Law 13.709/2018). The court of the user\'s domicile is hereby elected to resolve any disputes.',
        ],
      },
      {
        heading: '10. Contact',
        paragraphs: [
          'For questions related to these Terms, privacy or exercise of rights under the LGPD, contact us by email at web@felixtal.com.',
        ],
      },
    ],
  },
  privacy: {
    title: 'Privacy Policy',
    version: 'Version 1.2',
    effectiveDate: 'Effective date: 2026-10-08',
    draftNotice: 'This document is a preliminary version and should be reviewed by a lawyer before official publication.',
    sections: [
      {
        heading: '1. Introduction',
        paragraphs: [
          'This Privacy Policy describes how Aura Nota ("we") processes personal data collected through its tax document capture and extraction platform, in compliance with the General Data Protection Law (LGPD, Law 13.709/2018).',
        ],
      },
      {
        heading: '2. Data collected',
        paragraphs: [
          'The Service processes tax documents submitted by users, from which the following personal data is extracted, among others:',
        ],
        list: [
          'CNPJ and CPF of issuers and recipients;',
          'Names or company names;',
          'Addresses;',
          'Financial values (totals, taxes, unitary);',
          'Issue and competency dates;',
          'Other fields present in NFS-e, NF-e and telecommunications invoices.',
        ],
      },
      {
        heading: '3. Purpose of processing',
        paragraphs: [
          'The data is processed for the purpose of automated extraction of tax document fields, organization and export of extracted data, and self-learning: human corrections feed the improvement of extraction models, enhancing future accuracy.',
        ],
      },
      {
        heading: '4. Legal basis',
        paragraphs: [
          'Aura Nota acts in two roles: (a) controller of registration, access, log and billing data - processed under contract performance (Art. 7, item V, of the LGPD), legitimate interest (Art. 7, item IX - security and fraud prevention) and legal obligation (Art. 7, item II); (b) processor of the content of tax documents submitted by organizations, processed exclusively according to the controlling organization\'s instructions (Arts. 39 to 42 of the LGPD).',
          'User consent is recorded for the Terms of Use, this Policy and the Cookies Policy (Art. 8 of the LGPD); acceptance of the Custody Terms is recorded separately, with user identification, date, time and document version.',
        ],
      },
      {
        heading: '5. Data sharing',
        paragraphs: [
          'We do not share personal data with third parties, except infrastructure partners essential to the operation and when required by legal obligation or court order. Data is not sold or transferred for marketing purposes.',
          'The infrastructure sub-processors currently used are: (i) Contabo GmbH — VPS hosting in France (European Union; datacenter in Lauterbourg operated by sub-processor Contabo France SAS), where all Service data is stored, under a Data Processing Agreement (DPA); (ii) Cloudflare, Inc. — serves the public static frontend files (Cloudflare Pages) and answers DNS; acts as a pass-through processor, transmitting requests and responses only in transit without storing content, under a DPA incorporated into the contract with Standard Contractual Clauses (SCCs) and EU-US Data Privacy Framework certification.',
        ],
      },
      {
        heading: '6. International transfer',
        paragraphs: [
          'The Service infrastructure (Contabo GmbH VPS server) is located in France, in the European Union, a jurisdiction with a recognized adequate level of protection, and is where all personal data is hosted. Cloudflare serves only the public static frontend files (Cloudflare Pages) and DNS answers, acting as a pass-through processor: requests and responses transit the Cloudflare network without content storage. International transfer of personal data is carried out with adequate safeguards, through a Data Processing Agreement (DPA), Standard Contractual Clauses (SCC) and, in Cloudflare\'s case, EU-US Data Privacy Framework certification, in compliance with Art. 33 of the LGPD and the ANPD International Data Transfer Regulation (Resolution CD/ANPD No. 19/2024).',
        ],
      },
      {
        heading: '7. Retention',
        paragraphs: ['The retention periods are as follows:'],
        list: [
          'Processing failures: 24 hours for diagnosis;',
          'Self-learning corpus: 90 days;',
          'Backups: 7 days.',
        ],
      },
      {
        heading: '8. Security',
        paragraphs: ['We adopt the following technical and organizational security measures:'],
        list: [
          'JWT (JSON Web Token) authentication;',
          'Encryption of data in transit (TLS) and at rest;',
          'AIDE (Advanced Intrusion Detection Environment) for integrity;',
          'Restricted file permissions and principle of least privilege;',
          'Service execution with non-root user.',
        ],
      },
      {
        heading: '9. Anonymization',
        paragraphs: [
          'Before prolonged storage, identifiable personal data (PII) is masked. The document identifier (doc_id) is stored in hash form, so that the original data cannot be reconstructed from the persisted record.',
        ],
      },
      {
        heading: '10. Data subject rights',
        paragraphs: ['Under Arts. 17 to 22 of the LGPD, the data subject has the following rights:'],
        list: [
          'Confirmation of the existence of processing and access to data;',
          'Rectification of incomplete, inaccurate or outdated data;',
          'Elimination of personal data processed with consent;',
          'Right to be forgotten (portability and elimination);',
          'Information about sharing and purpose.',
        ],
      },
      {
        heading: '11. Data Protection Officer (DPO)',
        paragraphs: [
          "Felixtal's Data Protection Officer (CNPJ 07.888.306/0001-75) can be contacted by email at web@felixtal.com.",
        ],
      },
      {
        heading: '12. Impact assessment (DPIA)',
        paragraphs: [
          'A Data Protection Impact Assessment (DPIA) has been prepared and is available upon request to the DPO. The document describes the risks and mitigating measures adopted for the processing of sensitive tax data.',
        ],
      },
      {
        heading: '13. Cookies',
        paragraphs: [
          'The use of cookies is described in detail in the Cookies Policy, available at /cookies.',
        ],
      },
      {
        heading: '14. Changes to this policy',
        paragraphs: [
          'This Policy may be updated periodically. Material changes will be communicated and may require new consent. The current version is always the one available on this page, with the effective date indicated.',
        ],
      },
    ],
  },
  cookies: {
    title: 'Cookies Policy',
    version: 'Version 1.0',
    effectiveDate: 'Effective date: 2026-08-18',
    draftNotice: 'This document is a preliminary version and should be reviewed by a lawyer before official publication.',
    sections: [
      {
        heading: '1. What are cookies',
        paragraphs: [
          'Cookies are small text files stored in the user\'s browser to maintain session information and preferences between requests. They are necessary for the basic operation of authenticated web applications.',
        ],
      },
      {
        heading: '2. Cookies used',
        paragraphs: ['Aura Nota uses only cookies essential to the operation of the Service:'],
        list: [
          'Session JWT / auth token: maintains the user\'s authenticated session after login. Without this cookie, it is not possible to access features that require authentication.',
        ],
      },
      {
        heading: '3. Third-party cookies',
        paragraphs: [
          'Aura Nota does not use third-party cookies. We do not use third-party analytics, tracking or advertising tools that store cookies in your browser.',
        ],
      },
      {
        heading: '4. Cookie management',
        paragraphs: [
          'You can disable or remove cookies at any time through your browser settings. It is worth noting that disabling the session cookie will prevent access to the authenticated features of the Service.',
        ],
      },
      {
        heading: '5. Updates',
        paragraphs: [
          'This Cookies Policy may be updated if the Service starts using new cookies. Changes will be communicated on this page, with the effective date indicated.',
        ],
      },
      {
        heading: '6. DPO contact',
        paragraphs: [
          'For questions about this cookies policy or your privacy rights, contact our Data Protection Officer (DPO): web@felixtal.com.',
        ],
      },
    ],
  },
  custodia: {
    title: 'A1 Digital Certificate Custody Terms',
    version: 'Version 1.0',
    effectiveDate: 'Effective date: 2026-09-16',
    draftNotice: 'This document is a preliminary version and must be reviewed by a lawyer before official publication.',
    sections: [
      {
        heading: '1. Purpose and dedicated acceptance',
        paragraphs: [
          "These Terms govern the custody, by the Aura Nota platform, of your organization's A1 digital certificate (e-CNPJ), required for authenticated queries to SEFAZ/RFB portals (access-key lookup and electronic fiscal document distribution).",
          'This acceptance is autonomous and additional to the Terms of Use and the Privacy Policy. Custody only occurs after express acceptance of this term by an administrator or owner of the organization.',
          "The electronic acceptance of this Term is recorded with the user's identification, date, time and document version, constituting a valid manifestation of will and proof of consent (Art. 8 of the LGPD; Art. 10, §2, of MP 2.200-2/2001).",
        ],
      },
      {
        heading: '2. What is held in custody',
        paragraphs: [
          'The certificate file (.pfx/.p12) and its password, both encrypted at rest with strong encryption and a dedicated key, stored in the platform database.',
          'Certificate material is never displayed, exported, or returned in any platform response after upload.',
        ],
      },
      {
        heading: '3. How the certificate is used',
        paragraphs: [
          'The certificate is used exclusively to open ephemeral sessions in the fiscal integration service, which keeps it in memory only for the session duration and discards it afterwards - with no persistence.',
          'Sessions authenticate SEFAZ/RFB queries on behalf of the organization that owns the certificate. No other use is performed.',
          'The custodied certificate is not used to sign fiscal documents or events - including NF-e, CT-e, NFS-e, recipient manifestation events and correction letters - nor for any act requiring digital signature. Its use is restricted to session authentication for queries to SEFAZ/RFB.',
        ],
      },
      {
        heading: '4. LGPD roles',
        paragraphs: [
          'The organization that owns the certificate is the controller of the credential. Aura Nota acts as processor, handling the material exclusively under these Terms, through the fiscal integration service - an internal platform component that accesses the material only in memory and transiently.',
        ],
      },
      {
        heading: '5. Security and access control',
        list: [
          'Only organization administrators and owners can upload, rotate, or remove the certificate.',
          'Every action on the certificate is recorded in an audit trail (who, when, what), and consents are recorded with date and IP.',
          'Uploads are rate-limited and validated before custody.',
          'Expiry alerts are issued 30, 15, and 7 days before expiration.',
          "The certificate holder's CNPJ is validated against the organization's CNPJ before custody; a mismatch blocks the upload.",
        ],
      },
      {
        heading: '6. Responsibilities and limits',
        list: [
          'The organization declares that the user performing the acceptance has powers to bind it, that the certificate was issued by an ICP-Brasil Certification Authority in the name of its CNPJ, and that its submission is duly authorized;',
          "Management of the certificate's lifecycle - including renewal and revocation with the Certification Authority - is the organization's sole responsibility;",
          'Authenticated queries performed with the certificate constitute acts of the organization before SEFAZ/RFB; Aura Nota is not liable for unavailability, refusal or limitations of SEFAZ/RFB services - including document distribution windows - nor for the organization\'s fiscal deadlines and obligations.',
        ],
      },
      {
        heading: '7. Security incidents',
        paragraphs: [
          "In the event of a security incident that may compromise the custodied material, Aura Nota will notify the organization without undue delay, informing the nature of the incident and recommended measures - including revocation of the certificate with the Certification Authority, which is the organization's responsibility. Notification to the ANPD and data subjects will occur when required by Art. 48 of the LGPD.",
        ],
      },
      {
        heading: '8. Revocation and removal',
        paragraphs: [
          'The organization may remove the certificate at any time. Removal irreversibly erases the encrypted material, preserving only metadata and the audit trail, and immediately ends authenticated query capability.',
          'Upon termination of the account or the contractual relationship, the certificate will be automatically removed, with the same effects as this section, upon prior notice to the organization.',
        ],
      },
      {
        heading: '9. Term',
        paragraphs: [
          'These Terms remain in effect while a certificate is held in custody; the current version is recorded at each acceptance and material changes require a new acceptance. Removing the certificate ends custody, preserving metadata and audit trail for 5 (five) years or the applicable legal period, whichever is longer.',
        ],
      },
    ],
  },
};
